/* ======================================================
   CONFIG
====================================================== */

const EXPRESS_TODAY_VARIANT_ID    = 51763209765184;
const EXPRESS_TOMORROW_VARIANT_ID = 51763209797952;
const EXPRESS_SHIPPING_HANDLE = 'express-shipping';

const EXPRESS_VARIANT_IDS = [
  EXPRESS_TODAY_VARIANT_ID,
  EXPRESS_TOMORROW_VARIANT_ID
];

const CITY_TYPING_DELAY = 600;

/* ======================================================
   STATE
====================================================== */

let isBusy = false;
let expressEnabled = false;
let selectedCity = null;
let cityTypingTimer = null;
let expressProductCache = null;

/* ======================================================
   TIME HELPERS (ISRAEL)
====================================================== */

function getIsraelDate() {
  return new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Jerusalem' })
  );
}

function isBefore11() {
  return getIsraelDate().getHours() < 10.5;
}

function getIsraelDay() {
  return getIsraelDate().getDay();
}

/* ======================================================
   EXPRESS DAY LOGIC
====================================================== */

function getAvailableExpressMethods() {
  const day = getIsraelDay();
  const before11 = isBefore11();

  if (day === 4) return before11 ? ['today'/*, 'sunday'*/] : ['sunday'];
  if (day === 5) return ['sunday'];
  if (day === 6) return ['tomorrow'];

  return before11 ? ['today'/*, 'tomorrow'*/] : ['tomorrow'];
}

/* ======================================================
   CITY LIST
====================================================== */

function isCitySupported(cityName) {
  if (!window.SHIPPING_CITIES) return false;
  return window.SHIPPING_CITIES.some(c => c.city_name === cityName);
}

/* ======================================================
   CART HELPERS
====================================================== */

async function getCart() {
  return fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
}

async function addVariant(id) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, quantity: 1 })
  });
}

async function removeExpressVariants(cart) {
  const items = cart.items.filter(i =>
    EXPRESS_VARIANT_IDS.includes(i.variant_id)
  );

  if (items.length === 0) return;

  for (const item of items) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 })
    });
  }

  resetExpressRadios();
}

/* ======================================================
   CART ACTION WRAPPER
====================================================== */

async function cartAction(fn) {
  if (isBusy) return;
  isBusy = true;

  try {
    await fn();
    await new Promise(r => setTimeout(r, 120));

    if (location.pathname.startsWith('/cart')) {
      await refreshCartSection();
    }
  } finally {
    isBusy = false;
  }
}

/* ======================================================
   CART UI REFRESH
====================================================== */

async function refreshCartSection() {
  const wrapper = document.getElementById('main-cart-items');
  if (!wrapper?.dataset.id) return;

  const res = await fetch(`/cart?section_id=${wrapper.dataset.id}`);
  if (!res.ok) return;

  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const newItems = doc.querySelector('#main-cart-items');

  if (newItems) wrapper.innerHTML = newItems.innerHTML;
}

function resetExpressRadios() {
  const radios = document.querySelectorAll('input[name="express_method"]');  

  radios.forEach(r => r.checked = false);

  const cityInput = document.getElementById('shipping-city-input');
  
  if (cityInput) {
    cityInput.value = '';
  }

  const methodsBox = document.getElementById('express-methods');
  if (methodsBox) {
    methodsBox.style.display = 'none';
  }

  selectedCity = null;
}

/* ======================================================
   GLOBAL CART SANITY CHECK  🔑
====================================================== */

async function enforceExpressRules() {
  const cart = await getCart();

  const regularItems = cart.items.filter(
    i => !EXPRESS_VARIANT_IDS.includes(i.variant_id)
  );

  const hasExpress = cart.items.some(i =>
    EXPRESS_VARIANT_IDS.includes(i.variant_id)
  );

  // ❌ אין מוצרים רגילים → מוחקים משלוח
  if (hasExpress && regularItems.length === 0) {
    await removeExpressVariants(cart);
    return;
  }

  // ❌ עיר לא נבחרה / לא נתמכת → מוחקים משלוח
  if (
    hasExpress &&
    (!selectedCity || !isCitySupported(selectedCity))
  ) {
    await removeExpressVariants(cart);
  }

    // ✅ רענון UI של העגלה
  if (location.pathname.startsWith('/cart')) {
      await refreshCartSection();
  }
}

/* ======================================================
   EXPRESS PRICES UI
====================================================== */

async function loadExpressProduct() {
  if (expressProductCache) return expressProductCache;

  const res = await fetch(`/products/${EXPRESS_SHIPPING_HANDLE}.js`);
  if (!res.ok) return null;

  expressProductCache = await res.json();
  return expressProductCache;
}

async function updateExpressPricesUI() {
  const product = await loadExpressProduct();
  if (!product) return;

  product.variants.forEach(v => {
    if (EXPRESS_VARIANT_IDS.includes(v.id)) {
      const method =
        v.id === EXPRESS_TODAY_VARIANT_ID ? 'today' : 'tomorrow';

      const el = document.querySelector(
        `.express-price[data-method="${method}"]`
      );
      if (el) el.textContent = `₪${v.price / 100}`;
    }
  });
}

/* ======================================================
   DOM READY
====================================================== */

document.addEventListener('DOMContentLoaded', () => {

  const toggleBtn = document.getElementById('express-toggle-btn');
  const flowBox   = document.getElementById('express-flow');
  const cityInput = document.getElementById('shipping-city-input');
  const suggBox   = document.getElementById('city-suggestions');
  const methodsBox= document.getElementById('express-methods');
  const errorBox  = document.getElementById('express-error');
  const radios    = document.querySelectorAll('input[name="express_method"]');

  /* ---------- TOGGLE ---------- */

  toggleBtn?.addEventListener('click', async () => {
    expressEnabled = true;
    flowBox.style.display = 'block';
    toggleBtn.style.display = 'none';
    await updateExpressPricesUI();
  });

  /* ---------- CITY INPUT ---------- */

  cityInput?.addEventListener('input', () => {
    const value = cityInput.value.trim();
    suggBox.innerHTML = '';
    methodsBox.style.display = 'none';
    errorBox.style.display = 'none';
    selectedCity = null;

    clearTimeout(cityTypingTimer);
    if (value.length < 2) return;

    const matches = window.SHIPPING_CITIES.filter(c =>
      c.city_name.toLowerCase().includes(value.toLowerCase())
    );

    matches.slice(0, 8).forEach(city => {
      const li = document.createElement('li');
      li.textContent = city.city_name;
      li.onclick = () => {
        clearTimeout(cityTypingTimer);
        cityInput.value = city.city_name;
        suggBox.innerHTML = '';
        selectedCity = city.city_name;
        expressEnabled = true;

        methodsBox.style.display = 'block';
        const available = getAvailableExpressMethods();

        radios.forEach(r => {
          r.checked = false;
          r.closest('label').style.display =
            available.includes(r.value) ? 'block' : 'none';
        });
      };
      suggBox.appendChild(li);
    });

    cityTypingTimer = setTimeout(async () => {
      if (!matches.length) {
        errorBox.style.display = 'block';
        selectedCity = null;
        await cartAction(enforceExpressRules);
      }
    }, CITY_TYPING_DELAY);
  });

  /* ---------- METHOD SELECTION ---------- */

  radios.forEach(radio => {
    radio.addEventListener('change', () => {
      if (!expressEnabled || /*!selectedCity ||*/ isBusy) return;

      const wantedVariant =
        radio.value === 'today'
          ? EXPRESS_TODAY_VARIANT_ID
          : EXPRESS_TOMORROW_VARIANT_ID;

      cartAction(async () => {
        const cart = await getCart();

        const regularItems = cart.items.filter(
          i => !EXPRESS_VARIANT_IDS.includes(i.variant_id)
        );

        if (!regularItems.length) {
          await removeExpressVariants(cart);
          return;
        }

        if (!isCitySupported(selectedCity)) {
          await removeExpressVariants(cart);
          return;
        }

        const existing = cart.items.find(i =>
          EXPRESS_VARIANT_IDS.includes(i.variant_id)
        );

        if (existing?.variant_id === wantedVariant) return;

        if (existing) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: existing.key, quantity: 0 })
          });
        }

        await addVariant(wantedVariant);
      });
    });
  });

  /* ---------- WATCH CART CHANGES ---------- */

  (function hookCartFetch() {
    const originalFetch = window.fetch;

    window.fetch = async function (...args) {
      const res = await originalFetch.apply(this, args);

      try {
        const url = args[0]?.toString?.() || '';

        if (
          url.includes('/cart/add') ||
          url.includes('/cart/change') ||
          url.includes('/cart/update')
        ) {
          setTimeout(async () => {
            const cart = await getCart();

            const hasExpress = cart.items.some(i =>
              EXPRESS_VARIANT_IDS.includes(i.variant_id)
            );

            const hasRegularItems = cart.items.some(i =>
              !EXPRESS_VARIANT_IDS.includes(i.variant_id)
            );

            // ❌ משלוח נמחק ידנית או אין מוצרים רגילים
            if (!hasExpress || !hasRegularItems) {
              resetExpressRadios();
            }
            await cartAction(enforceExpressRules);

            // UI visibility
            const card = document.querySelector('.shipping-card');
            if (card) {
              card.style.display = hasRegularItems ? 'block' : 'none';
            }

          }, 150);
        }
      } catch (e) {}

      return res;
    };
  })();

});